import React, { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Paper, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { pluralize, capitalize } from "inflection";
import csrfToken from "../lib/csrf-token";
import { mtsUrl, requestPath } from "../lib/mts-url";
import {
  RequestType,
  DesignProjectFields,
  WebUpdateFields,
  SocialMediaPostFields,
  NewRequestFormProps,
  EventFields,
  MarketingCampaignFields,
  BusinessCardFields,
  RequestBaseFields,
} from "../types/request.types";
import useTempId from "../hooks/use-temp-id";

type NewRequestFields =
  DesignProjectFields &
  WebUpdateFields &
  SocialMediaPostFields &
  EventFields &
  MarketingCampaignFields &
  BusinessCardFields;

interface NewRequestProps {
  formComponent: React.ComponentType<NewRequestFormProps>;
  requestType: RequestType;
}

export default function NewRequest(props: NewRequestProps) {
  const tempId = useTempId();
  const {
    formComponent: FormComponent,
    requestType
  } = props;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const reqTypeWords = requestType.split('_').slice(0, -1);
  const humanizedReqType = reqTypeWords.map(w => capitalize(w)).join(' ');
  const [searchParams] = useSearchParams();
  const fromType = searchParams.get('fromType');
  const fromId = searchParams.get('fromId');
  let fromQuery;
  let fromRequest: RequestBaseFields | undefined;

  if (fromType && fromId) {
    fromQuery = useQuery({
      queryKey: [fromType, fromId],
      queryFn: () => fetch(`/${requestPath(fromType as RequestType, fromId)}.json`)
        .then(r => r.json())
    });

    fromRequest = fromQuery.data;
  }

  const addRequest = useMutation({
    mutationFn:  (newRequest: NewRequestFields) => {
      const pathBase = pluralize(reqTypeWords.join('_'));

      return axios.post(
        mtsUrl(`${pathBase}/create`),
        {
          [requestType]: newRequest,
          temp_attachable_id: tempId,
          from_request_id: fromId,
          from_request_type: fromType,
        },
        {
          withCredentials: true,
          headers: { 'X-CSRF-Token': csrfToken() }
        }
      )
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['requests', 'all'] });
      queryClient.invalidateQueries({
        queryKey: ['requests', `type:${requestType}`]
      });

      if (fromType) {
        queryClient.invalidateQueries({
          queryKey: ['requests', `type:${fromType}`]
        });
      }
      
      notifications.show({
        message: `The ${humanizedReqType} was successfully created.`,
        icon: <IconCheck />,
        color: 'teal',
        position: 'top-center'
      });

      navigate('/requests');
    },
    onError: (error, variables, context) => {
      notifications.show({
        message: `There was an error creating the ${humanizedReqType}.`,
        icon: <IconX />,
        color: 'red',
        position: 'top-center'
      });
    }
  });

  const onFormSubmit = useCallback((newRequest: NewRequestFields) => {
    addRequest.mutate(newRequest);
  }, [addRequest]);
  
  return (
    <Paper p={18} radius={0} mih="100vh">
      <Title order={1}>New {humanizedReqType}</Title>
      {fromQuery?.isSuccess && fromRequest && (
        <div>
          <Text fs="italic" my="sm" size="sm" c="gray.8">
            Converting from&nbsp;
            <Text fw="bolder" component="span">
              {fromType?.split('_').map(w => capitalize(w)).join(' ')}:&nbsp;
              {fromRequest.name}
            </Text>.
            All assignments, contacts, attachments, and comments will be migrated.
          </Text>
        </div>
      )}
      <FormComponent
        onSubmit={onFormSubmit}
        tempId={tempId}
        fromRequest={fromRequest}
      />
    </Paper>
  );
}